import { Button } from 'components/ui/button';
import useRemoveFavoriteDocument from 'domain/documents/hooks/useRemoveFavoriteDocument';
import useRemoveRecentDocument from 'domain/documents/hooks/useRemoveRecentDocument';
import { DocumentLink } from 'domain/documents/schemas/document';
import { Download, ExternalLink, File, Trash } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useDownload from 'utils/useDownload';

interface RecentDocumentDropDownItemProps {
  document: DocumentLink;
  type: 'favorite' | 'recent';
  index: number;
  setIsOpen: (isOpen: boolean) => void;
  canShowDropdown: boolean;
}

export default function RecentDocumentDropDownItem({
  document,
  type,
  index,
  setIsOpen,
  canShowDropdown,
}: RecentDocumentDropDownItemProps) {
  const { t } = useTranslation('searchPage');

  // Les deux retraits partagent la même signature ; seul le lien visé change.
  const removeFavorite = useRemoveFavoriteDocument();
  const removeRecent = useRemoveRecentDocument();
  const { mutate: removeDocument, isPending: isRemoving } = type === 'favorite' ? removeFavorite : removeRecent;

  const { download, isDownloading } = useDownload();

  const hasFile = document.downloadUrl !== '';
  // Un document qui pointe ailleurs s'ouvre dans une nouvelle page au lieu de se télécharger.
  const hasLink = document.linkUrl !== '';

  const handleDownloadClick = () => {
    setIsOpen(false);
    download(document.downloadUrl, document.filename);
  };

  return (
    <div
      className={[
        'group flex items-center justify-between gap-3 rounded-xl px-3 py-2',
        'bg-white ease-in-out hover:bg-gray-200',
        'transition-all duration-200',
        canShowDropdown ? 'translate-x-0 opacity-100' : 'translate-x-1 opacity-0',
      ].join(' ')}
      style={{ transitionDelay: `${index * 35}ms` }}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl">
          <File className="h-4 w-4 text-gray-700" />
        </span>

        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">{document.title}</p>
          <p className="text-xs text-gray-500">{hasLink ? t('suggestion.link') : t('suggestion.document')}</p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {hasLink ? (
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full border transition hover:border-secondaryYellow hover:bg-secondaryYellow/50 active:scale-95"
          >
            <a
              href={document.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('suggestion.ariaLabelLink', { title: document.title })}
              onClick={() => setIsOpen(false)}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full border transition hover:border-secondaryYellow hover:bg-secondaryYellow/50 active:scale-95"
            aria-label={t('suggestion.ariaLabelDownload', { title: document.title })}
            onClick={handleDownloadClick}
            disabled={!hasFile || isDownloading}
          >
            <Download className="h-4 w-4" />
          </Button>
        )}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full border transition hover:border-red-300 hover:bg-red-100 active:scale-95 disabled:opacity-60"
          onClick={() => removeDocument(document)}
          disabled={isRemoving}
          aria-label={t('suggestion.ariaLabelRecent', { title: document.title })}
        >
          <Trash className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}
